import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Select, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateCountryLookupListFormValues } from '../../../../../interfaces/lookup/country';
import { IUpdateCountry } from '../../../../../interfaces/lookup/country/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchCountryById, fetchCurrenciesLookupList, updateCountry } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CountryLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const { currencies } = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateCountryLookupListFormValues) => {
        const body: IUpdateCountry = {
            id: lookupSlice.detailsPage.country.data?.id,
            ...(values as any),
        };

        dispatch(updateCountry(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.country.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/country', { replace: true });
        }
    }, [lookupSlice.detailsPage.country.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCountryById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.country.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.country.data.id,
                code: lookupSlice.detailsPage.country.data.code,
                description: lookupSlice.detailsPage.country.data.description,
                defaultCurrencyId: lookupSlice.detailsPage.country.data.defaultCurrency?.id,
            } as IUpdateCountryLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.country.data]);

    useEffect(() => {
        dispatch(fetchCurrenciesLookupList());
    }, []);

    if (lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="country-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage country lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Country',
                        path: '/admin/lookup-lists/country',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.country.data?.description || '-',
                        path: `/admin/lookup-lists/country/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/country/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.country.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.country.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.country.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.country.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.country.fetchStatus} about={'Country'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Country Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Default currency</label>
                                    <Form.Item
                                        name="defaultCurrencyId"
                                        rules={[{ required: true, message: "'Default currency' is required" }]}
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Select a currency"
                                            filterOption={(input, option) =>
                                                (option!.children as unknown as string).toLowerCase().startsWith(input.toLowerCase())
                                            }
                                        >
                                            {currencies.lookup.data.map((currency) => (
                                                <Select.Option key={currency.key} value={currency.key} disabled={currency.isDeleted}>
                                                    {currency.description}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.country?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.country.data?.created
                                            ? dayjs(lookupSlice.detailsPage.country.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.country.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.country.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.country.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
